import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Database, Zap, Clock, TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react';
import { lambdaEndpoints } from '../../aws-config';
import { TopNav } from '../ui/top-nav';

interface StorageUsage {
  original_gb: number;
  compressed_gb: number;
  limit_gb: number;
  usage_percent: number;
  warning: boolean;
  blocked: boolean;
}

interface QueryUsage {
  total: number;
  ui: number;
  mcp: number;
  limit?: number;
  usage_percent?: number;
  warning?: boolean;
  blocked?: boolean;
}

interface UsageData {
  user_id: string;
  tier: string;
  storage: StorageUsage;
  queries: {
    daily: QueryUsage;
    monthly: QueryUsage;
    alltime: QueryUsage;
  };
  period: {
    date: string;
    month: string;
  };
  timestamp: string;
}

export const UsagePage: React.FC = () => {
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if usage API is configured
      if (!lambdaEndpoints.usageApi) {
        // Generate mock data for now
        const mockData: UsageData = {
          user_id: 'demo-user',
          tier: 'free',
          storage: {
            original_gb: 1.25,
            compressed_gb: 0.45,
            limit_gb: 5,
            usage_percent: 25,
            warning: false,
            blocked: false,
          },
          queries: {
            daily: { total: 15, ui: 10, mcp: 5, limit: 100, usage_percent: 15, warning: false, blocked: false },
            monthly: { total: 250, ui: 180, mcp: 70, limit: 1000, usage_percent: 25, warning: false, blocked: false },
            alltime: { total: 1250, ui: 950, mcp: 300 },
          },
          period: {
            date: new Date().toISOString().split('T')[0],
            month: new Date().toISOString().slice(0, 7),
          },
          timestamp: new Date().toISOString(),
        };
        setUsage(mockData);
        setIsLoading(false);
        return;
      }

      // Fetch real usage data from API
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${lambdaEndpoints.usageApi}/usage`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage data: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.usage) {
        setUsage(data.usage);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      console.error('Error loading usage data:', err);
      setError(err.message || 'Failed to load usage data');
      toast.error('Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'pro':
        return 'bg-blue-500';
      case 'enterprise':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getProgressColor = (percent: number, warning: boolean, blocked: boolean) => {
    if (blocked) return 'bg-red-500';
    if (warning) return 'bg-yellow-500';
    if (percent > 80) return 'bg-yellow-500';
    if (percent > 0) return 'bg-green-500';
    return 'bg-muted-foreground/30'; // Gray when no usage
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !usage) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
            <p className="text-lg text-muted-foreground">{error}</p>
            <Button onClick={loadUsageData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6 space-y-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-base font-semibold text-foreground">Usage & Limits</h1>
              <p className="text-sm text-muted-foreground">Monitor your BigSplice usage</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getTierBadgeColor(usage?.tier || 'free')}>
              {(usage?.tier || 'free').toUpperCase()} TIER
            </Badge>
            <Button variant="outline" size="sm" onClick={loadUsageData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {usage && (
          <div className="space-y-6">
            {/* Storage Usage Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Storage Usage
                </CardTitle>
                <CardDescription>
                  Your uploaded data storage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Original Data Size</span>
                    <span className="font-medium">
                      {usage.storage.original_gb.toFixed(2)} GB / {usage.storage.limit_gb} GB
                    </span>
                  </div>
                  <Progress
                    value={usage.storage.usage_percent}
                    className="h-3"
                    indicatorClassName={getProgressColor(usage.storage.usage_percent, usage.storage.warning, usage.storage.blocked)}
                  />
                  {usage.storage.warning && !usage.storage.blocked && (
                    <div className="flex items-center gap-2 text-yellow-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      You're approaching your storage limit. Consider freeing up space or upgrading your plan.
                    </div>
                  )}
                  {usage.storage.blocked && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      You've reached your storage limit. Please delete some files or upgrade your plan to continue uploading.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">Original Size</p>
                    <p className="text-lg font-semibold">{usage.storage.original_gb.toFixed(3)} GB</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Compressed Size</p>
                    <p className="text-lg font-semibold">{usage.storage.compressed_gb.toFixed(3)} GB</p>
                    <p className="text-xs text-muted-foreground">
                      {usage.storage.original_gb > 0
                        ? `${((1 - usage.storage.compressed_gb / usage.storage.original_gb) * 100).toFixed(0)}% compression`
                        : 'No data uploaded'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Query Usage - Daily */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Daily Query Usage
                </CardTitle>
                <CardDescription>
                  Queries used today ({usage.period.date})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Queries Today</span>
                    <span className="font-medium">
                      {usage.queries.daily.total} / {usage.queries.daily.limit}
                    </span>
                  </div>
                  <Progress
                    value={usage.queries.daily.usage_percent || 0}
                    className="h-3"
                    indicatorClassName={getProgressColor(
                      usage.queries.daily.usage_percent || 0,
                      usage.queries.daily.warning || false,
                      usage.queries.daily.blocked || false
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">UI Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.daily.ui}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MCP Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.daily.mcp}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Query Usage - Monthly */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Monthly Query Usage
                </CardTitle>
                <CardDescription>
                  Queries used this month ({usage.period.month})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Queries This Month</span>
                    <span className="font-medium">
                      {usage.queries.monthly.total} / {usage.queries.monthly.limit}
                    </span>
                  </div>
                  <Progress
                    value={usage.queries.monthly.usage_percent || 0}
                    className="h-3"
                    indicatorClassName={getProgressColor(
                      usage.queries.monthly.usage_percent || 0,
                      usage.queries.monthly.warning || false,
                      usage.queries.monthly.blocked || false
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">UI Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.monthly.ui}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MCP Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.monthly.mcp}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* All-Time Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  All-Time Statistics
                </CardTitle>
                <CardDescription>
                  Your total usage history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{usage.queries.alltime.total.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Queries</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{usage.queries.alltime.ui.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">UI Queries</p>
                  </div>
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{usage.queries.alltime.mcp.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">MCP Queries</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tier Information */}
            <Card>
              <CardHeader>
                <CardTitle>Your Plan</CardTitle>
                <CardDescription>Current limits for {usage.tier} tier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Storage Limit</p>
                    <p className="text-lg font-semibold">{usage.storage.limit_gb} GB</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Daily Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.daily.limit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Queries</p>
                    <p className="text-lg font-semibold">{usage.queries.monthly.limit}</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Need more capacity? Contact us at{' '}
                    <a href="mailto:support@bigsplice.com" className="text-primary hover:underline">
                      support@bigsplice.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Last Updated */}
            <p className="text-xs text-center text-muted-foreground">
              Last updated: {new Date(usage.timestamp).toLocaleString()}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};
